"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import useMeasure from "react-use-measure";

export const Reviews = ({ background }: { background: boolean }) => {
  const [refCard, { width }] = useMeasure();
  const [maxWidth, setMaxWidth] = useState(`${width * 4 + 16 * 3}px`);
  const [elementInView, setElementInView] = useState(4);
  const [order, setOrder] = useState<number>(0);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  useEffect(() => {
    const updateMaxWidth = () => {
      if (window.innerWidth <= 768) {
        setMaxWidth(`${width}px`);
        setElementInView(1);
      } else if (window.innerWidth > 768 && window.innerWidth <= 1024) {
        setMaxWidth(`${width * 2 + 16 * 1}px`);
        setElementInView(2);
      } else if (window.innerWidth > 1024 && window.innerWidth <= 1280) {
        setMaxWidth(`${width * 3 + 16 * 2}px`);
        setElementInView(3);
      } else {
        setMaxWidth(`${width * 4 + 16 * 3}px`);
        setElementInView(4);
      }
    };

    updateMaxWidth();
    window.addEventListener("resize", updateMaxWidth);

    return () => window.removeEventListener("resize", updateMaxWidth);
  }, [width]);

  return (
    <div
      ref={ref}
      className={`w-full h-fit flex items-center justify-center ${background ? "bg-natura-lighter-2" : "bg-transparent"} px-6 py-12 lg:pt-14 lg:pb-28`}
    >
      <div className="container mx-auto flex flex-col gap-12 md:gap-16 items-center">
        {/* Heading */}
        <div className="flex flex-col gap-4 items-center">
          {/* Title */}
          <div className="flex flex-col gap-1.5 w-full items-center">
            <div className="flex">
              {[...Array(5)].map((_, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-center p-1.5 transition-all duration-300 ease-out
                      ${inView ? "scale-100" : "scale-0"}
                    `}
                  style={{
                    transitionDelay: `${0.1 * (idx + 1)}s`,
                  }}
                >
                  <svg
                    className="size-7 md:size-9"
                    viewBox="0 0 38 37"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.0979 1.8541C17.6966 0.0114822 20.3034 0.0114799 20.9021 1.8541L23.9393 11.2016C24.2071 12.0257 24.975 12.5836 25.8414 12.5836H35.67C37.6074 12.5836 38.413 15.0628 36.8456 16.2016L28.8941 21.9787C28.1931 22.488 27.8998 23.3907 28.1675 24.2148L31.2047 33.5623C31.8034 35.4049 29.6945 36.9372 28.127 35.7984L20.1756 30.0213C19.4746 29.512 18.5254 29.512 17.8244 30.0213L9.87295 35.7984C8.30553 36.9372 6.19656 35.4049 6.79527 33.5623L9.83246 24.2148C10.1002 23.3907 9.80689 22.488 9.10592 21.9787L1.15444 16.2016C-0.412985 15.0628 0.392565 12.5836 2.33001 12.5836H12.1586C13.025 12.5836 13.7929 12.0257 14.0607 11.2016L17.0979 1.8541Z"
                      fill="#D99329"
                    />
                  </svg>
                </div>
              ))}
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serifText text-natura-primary text-center">
              3K+ Google Reviews
            </h2>
          </div>
          <p className="text-base md:text-base lg:text-lg text-center max-w-[870px] font-nunito px-6">
              Lebih dari 3.000 Review Organik di Google, 99% Kasih Bintang 5★ untuk Natura.
          </p>
        </div>
        {/* Testimonial */}
        <div className="flex flex-col gap-8 md:gap-12 max-w-[1268px] items-center justify-center">
          <div className="flex gap-4 items-center px-6">
            {/* Arrow left */}
            <button
              className={`size-6 md:size-10 bg-white rounded-full flex items-center justify-center py-3 md:py-5 px-1.5 md:px-2.5 cursor-pointer ${order === 0 ? "opacity-50 cursor-not-allowed" : "hover:bg-natura-primary"} group`}
              onClick={() => setOrder((prev) => (prev - 1 < 0 ? 0 : prev - 1))}
              disabled={order === 0}
            >
              <svg
                className={`size-3 md:size-6 ${order === 0 ? "" : "group-hover:fill-natura-white"} fill-natura-primary `}
                viewBox="0 0 24 23"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0.93934 10.4393C0.353553 11.0251 0.353553 11.9749 0.93934 12.5607L10.4853 22.1066C11.0711 22.6924 12.0208 22.6924 12.6066 22.1066C13.1924 21.5208 13.1924 20.5711 12.6066 19.9853L4.12132 11.5L12.6066 3.01472C13.1924 2.42893 13.1924 1.47919 12.6066 0.893398C12.0208 0.307611 11.0711 0.307611 10.4853 0.893398L0.93934 10.4393ZM22 13C22.8284 13 23.5 12.3284 23.5 11.5C23.5 10.6716 22.8284 10 22 10V13ZM2 13H22V10H2V13Z" />
              </svg>
            </button>

            <div
              className={`flex gap-4 overflow-hidden`}
              style={{
                maxWidth,
              }}
            >
              {/* Card */}
              {reviews.map((item, idx_card) => (
                <a
                  key={idx_card}
                  href={item.link}
                  ref={refCard}
                  target="_blank"
                  className={`relative group flex flex-col gap-4 p-6 bg-white rounded-xl border-transparent transition-all duration-1000 ease-out${
                    width > 0 ? "opacity-100" : "opacity-0"
                  } ${!background && "border-[2px] border-natura-primary box-content"}`}
                  style={{
                    translate:
                      width > 0 ? `-${order * width + 16 * order}px` : "0px",
                  }}
                >
                  <div className="pointer-events-none absolute inset-0 rounded-xl border-4 border-natura-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="rounded-full flex items-center justify-center size-[35px] md:size-[50px] bg-natura-primary">
                    <svg
                      className="w-[17.px] h-[14px] md:w-[26px] md:h-[22px]"
                      viewBox="0 0 26 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.53064 0.354867C4.16712 3.81984 1.18738 7.853 0.591435 12.4543C-0.336675 19.6172 6.03117 23.123 9.25783 19.9733C12.4845 16.8235 10.5506 12.8248 8.21433 11.7314C5.87803 10.638 4.44918 11.0192 4.69848 9.55826C4.94778 8.09733 8.27132 4.04841 11.1917 2.16259C11.295 2.07397 11.3609 1.94909 11.376 1.81333C11.3911 1.67758 11.3542 1.54114 11.2729 1.43176L10.5506 0.486703C10.2372 0.0768669 9.93664 0.0926295 9.53064 0.354151M23.6524 0.354867C18.2889 3.81984 15.3092 7.853 14.7132 12.4543C13.7844 19.6172 20.1523 23.123 23.3789 19.9733C26.6056 16.8235 24.6717 12.8248 22.3347 11.7314C19.9977 10.638 18.5696 11.0192 18.8189 9.55826C19.0682 8.09733 22.3924 4.04841 25.3128 2.16259C25.416 2.07387 25.4818 1.94894 25.4967 1.81319C25.5117 1.67744 25.4747 1.54106 25.3933 1.43176L24.6717 0.486703C24.3583 0.0768669 24.0577 0.0926295 23.6524 0.354151"
                        fill="#FFBD58"
                      />
                    </svg>
                  </div>

                  {/* Review */}
                  <div className="flex justify-center w-36 h-32 md:w-44 md:h-36 overflow-hidden">
                    <p className="text-xs md:text-sm text-black font-nunito line-clamp-[7]">
                      {item.content}
                    </p>
                  </div>

                  {/* Stars */}
                  <div className="flex flex-col gap-1.5">
                    <div className="flex">
                      {[...Array(5)].map((_, idx) => (
                        <div
                          key={idx}
                          className="flex size-5 md:size-6 items-center justify-center"
                        >
                          <svg
                            width="21"
                            height="19"
                            viewBox="0 0 21 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.54894 0.927049C9.8483 0.0057385 11.1517 0.0057404 11.4511 0.927051L13.0819 5.9463C13.2158 6.35833 13.5997 6.63729 14.033 6.63729H19.3105C20.2792 6.63729 20.682 7.8769 19.8983 8.4463L15.6287 11.5484C15.2782 11.803 15.1315 12.2544 15.2654 12.6664L16.8963 17.6857C17.1956 18.607 16.1411 19.3731 15.3574 18.8037L11.0878 15.7016C10.7373 15.447 10.2627 15.447 9.91221 15.7016L5.64258 18.8037C4.85887 19.3731 3.80439 18.607 4.10374 17.6857L5.7346 12.6664C5.86847 12.2544 5.72181 11.803 5.37132 11.5484L1.10169 8.4463C0.317977 7.8769 0.720754 6.63729 1.68948 6.63729H6.96703C7.40026 6.63729 7.78421 6.35833 7.91809 5.9463L9.54894 0.927049Z"
                              fill="#FFBD58"
                            />
                          </svg>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs md:text-sm font-bold font-nunito">
                      {item.client}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            {/* Arrow left */}
            <button
              className={`group size-6 md:size-10 bg-white rounded-full flex items-center justify-center py-3 md:py-5 px-1.5 md:px-2.5 cursor-pointer ${order === reviews.length - elementInView ? "opacity-50 cursor-not-allowed" : "hover:bg-natura-primary"}`}
              onClick={() =>
                setOrder((prev) =>
                  prev + 1 > reviews.length - elementInView
                    ? reviews.length - elementInView
                    : prev + 1
                )
              }
              disabled={order === reviews.length - elementInView}
            >
              <svg
                className={`size-3 md:size-6 ${order === reviews.length - elementInView ? "" : "group-hover:fill-natura-white"} fill-natura-primary `}
                viewBox="0 0 24 23"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M2 10C1.17157 10 0.5 10.6716 0.5 11.5C0.5 12.3284 1.17157 13 2 13V10ZM23.0607 12.5607C23.6464 11.9749 23.6464 11.0251 23.0607 10.4393L13.5147 0.893398C12.9289 0.307611 11.9792 0.307611 11.3934 0.893398C10.8076 1.47919 10.8076 2.42893 11.3934 3.01472L19.8787 11.5L11.3934 19.9853C10.8076 20.5711 10.8076 21.5208 11.3934 22.1066C11.9792 22.6924 12.9289 22.6924 13.5147 22.1066L23.0607 12.5607ZM2 13H22V10H2V13Z" />
              </svg>
            </button>
          </div>

          {/* Button  */}
          <Link
            href={"/testimonials"}
            className="flex items-center justify-center px-6 py-3 rounded-full bg-natura-primary hover:bg-natura-hover active:bg-natura-pressed transition-all duration-150 w-fit font-nunito text-white font-normal cursor-pointer text-xs md:text-base leading-normal"
          >            
              Lihat Testimoni Lainnya            
          </Link>
        </div>
      </div>
    </div>
  );
};

const reviews = [
  {
    content:
      "Perawatan behel di Natura sudah berjalan 2 tahun. Selama perawatan behel nyaman dan dokternya baik dan admin selalu mengingatkan jadwal kita kontrol. Nah, kemarin juga baru cabut gigi dengan drg Dwi pas dibius gak sakit, biasanya kan sakit banget tuh kalau pas dibius, dan sampe rumah sudah bisa langsung makan.",
    client: "Rouli Astini",
    link: "https://maps.app.goo.gl/eVp6L3vrn3DDUc2n6",
  },
  {
    content:
      "Melakukan pemasangan behel di Klinik Gigi Natura sangat nyaman. Alhamdulillah selalu mendapatkan perawatan yang terbaik bahkan saat memasang behel tidak ada sariawan. Kontrol gigi ke dokter yang berbeda pun sangat nyaman.",
    client: "Riani Dewi",
    link: "https://maps.app.goo.gl/C5rHzUNorTgJE4nm7",
  },
  {
    content:
      "Sudah +/- 1 tahun kami sekeluarga khususnya anak saya Putri Videlia mempercayakan pemasangan behel gigi serta perawatannya di klinik Natura yang ditangani langsung oleh dokter yang sangat profesional dan ramah yakni drg. Inez.",
    client: "Christian Erlambang",
    link: "https://maps.app.goo.gl/cr6rqjYgobsXPPi2A",
  },
  {
    content:
      "Sudah hampir 2 tahun kontrol setelah pemasangan behel pertama dan selalu nyaman setiap datang ke Natura. Fasilitas dan pelayanannya sangat bagus dan bisa bikin puas setiap kontrol. Dokter yang menangani juga sangat baik dan selalu ngasih edukasi kesehatan gigi. Bintang 5.",
    client: "Sadam Permana",
    link: "https://maps.app.goo.gl/siEGUxmbK8zTxLRF8",
  },
  {
    content:
      "Awal sebelum pemasangan gigi bawah saya berantakan. Setelah pemasangan damon behel dengan drg. Nin yang super baik & sabar, alhamdulillah gigi saya sekarang sudah rapi & kalau senyum sekarang jadi nambah percaya diri tentunya.",
    client: "Clara Vidhia",
    link: "https://maps.app.goo.gl/NqWaau8Sovrdu4WAA",
  },
  {
    content:
      "Menemani anak-anak perawatan gigi dengan drg. Atikah. Alhamdulillah dokter dan semua staf sabar menemani perawatan. Anak saya agak trauma ke dokter gigi dan bisa sukses perawatan di sini. Terima kasih Natura.",
    client: "Amanda Pattisahusiwa",
    link: "https://maps.app.goo.gl/F23UJJFv1ndwKghi6",
  },
  {
    content:
      "Kedua kalinya ke Natura Dental Clinic, untuk perawatan gigi anak. Dokternya baik dan ramah terutama ke anak-anak. Pada saat menjelaskan terkait perawatan gigi untuk anak-anak, dokter selalu meminta partisipasi anak dan memastikan mereka paham. Sukses selalu Natura Dental Klinik.",
    client: "Ratika Rahmasari",
    link: "https://maps.app.goo.gl/kNrMZhVpvqPKHheo8",
  },
  {
    content:
      "Pertama kali ke sini dari mulai resepsionis, suster dan dokter sangat ramah dan sabar. Alhamdulillah bisa nanganin anak aku yang suka drama kalo cabut gigi.",
    client: "Febri Nandini Argitani",
    link: "https://maps.app.goo.gl/gXKrghmeVoWgBSdr6",
  },
  {
    content:
      "Dokter dan perawatnya mampu membuat anak usia 6 tahun tenang. Klinik bersih, perawat dokter ramah banget.",
    client: "Sika Kribo",
    link: "https://maps.app.goo.gl/XbNsFP9daMUWsz7b7",
  },
];
